import { BourseVirtualStatus, ContributionVEFStatus, DemandesFinancementStatus, DemandesFinancementActions, } from '../types';
import DemandesApprenantsHelper from './demandes-apprenants-helper';
/**
 * DemandesFinancement formatter
 */
class DemandesApprenantsFormatter {
    i18nService;
    demandesFinancementFormatter;
    dateFormatterService;
    constructor({ i18nService, demandesFinancementFormatter, dateFormatterService }) {
        this.i18nService = i18nService;
        this.demandesFinancementFormatter = demandesFinancementFormatter;
        this.dateFormatterService = dateFormatterService;
    }
    formatIdentityName(tiers) {
        if (tiers) {
            return tiers.individu
                ? `${tiers.individu.Prenoms.PrenomUsuel} ${tiers.individu.Noms.NomUsage}`
                : `${tiers.title}`;
        }
        return '';
    }
    async formatIdentityDetails(tiers) {
        if (tiers) {
            const demandesApprenantsHelper = new DemandesApprenantsHelper(this.i18nService, tiers, this.dateFormatterService);
            return demandesApprenantsHelper.formatIdentityDetails();
        }
        return [];
    }
    formatHistoryDetails(formattedDemande) {
        const historyDetails = formattedDemande.virtualHistoryDetails?.map((virtualHistoryDetail) => {
            let variant;
            switch (virtualHistoryDetail.status) {
                case BourseVirtualStatus.ENTERED_FORMATION: {
                    variant = 'success';
                    break;
                }
                case BourseVirtualStatus.VERIFIED: {
                    variant = 'info';
                    break;
                }
                case BourseVirtualStatus.NOT_ENTERED_FORMATION: {
                    variant = 'danger';
                    break;
                }
                case DemandesFinancementStatus.closed:
                case DemandesFinancementStatus.transmitted: {
                    variant = 'secondary';
                    break;
                }
                case 'CONTRIBUTION_INFORMATION': {
                    variant = 'warning';
                    break;
                }
            }
            let tagContent = {};
            if (virtualHistoryDetail.customStatusTitle) {
                tagContent = {
                    tagContent: virtualHistoryDetail.customStatusTitle,
                };
            }
            else if (virtualHistoryDetail.status)
                tagContent = {
                    tagContent: this.i18nService.translate(`demandes-financement.virtual-history-status.${virtualHistoryDetail.status}`),
                };
            let tagVariant = {};
            if (variant)
                tagVariant = {
                    tag: {
                        soft: 'true',
                        variant,
                    },
                };
            return {
                content: virtualHistoryDetail.label,
                ...tagContent,
                ...tagVariant,
            };
        });
        return historyDetails ?? [];
    }
    formatMessages(formattedDemande) {
        let warningMessage;
        const messages = [];
        if (!formattedDemande.teleservice.validationEntreeFormation ||
            !formattedDemande.contributionVef ||
            formattedDemande.cloture?.value ||
            [ContributionVEFStatus.VALIDATED, ContributionVEFStatus.CLOSED].includes(formattedDemande.contributionVef.status)) {
            return messages;
        }
        const isContributionVEFRequested = formattedDemande.contributionVef.status === ContributionVEFStatus.REQUESTED;
        const isContributionVEFChecked = formattedDemande.contributionVef.status === ContributionVEFStatus.CHECKED;
        const isFormationAlreadyStarted = formattedDemande.bourse?.formationDateEntree &&
            new Date(formattedDemande.bourse.formationDateEntree) < new Date();
        if (isContributionVEFRequested && !isFormationAlreadyStarted) {
            warningMessage = this.i18nService.translate('demandes-apprenants.warningMessage.verificationRequired');
        }
        else if (isContributionVEFRequested && isFormationAlreadyStarted) {
            warningMessage = this.i18nService.translate('demandes-apprenants.warningMessage.verificationAndValidationRequired');
        }
        else if (isContributionVEFChecked && isFormationAlreadyStarted) {
            warningMessage = this.i18nService.translate('demandes-apprenants.warningMessage.validationRequired');
        }
        if (warningMessage) {
            messages.push({
                label: warningMessage,
                toggleClosed: this.i18nService.translate('common.close'),
                toggleOpened: this.i18nService.translate('common.open'),
                variant: 'warning',
            });
        }
        return messages;
    }
    async formatHistoryEvent(event) {
        let label = null;
        switch (event.status) {
            case DemandesFinancementStatus.transmitted:
            case BourseVirtualStatus.VERIFIED: {
                label = await this.demandesFinancementFormatter.translateEventDate('demandes-financement.at', event.date);
                break;
            }
            case BourseVirtualStatus.ENTERED_FORMATION: {
                label = await this.demandesFinancementFormatter.translateEventDate('demandes-financement.validated-at', event.date);
                break;
            }
            case BourseVirtualStatus.NOT_ENTERED_FORMATION: {
                label = await this.demandesFinancementFormatter.translateEventDate('demandes-financement.filled-at.feminine', event.date);
                break;
            }
            case 'CONTRIBUTION_INFORMATION': {
                label = await this.demandesFinancementFormatter.translateEventDate('demandes-financement.filled-at.masculine', event.date);
                break;
            }
        }
        if (!label) {
            label = event.status;
        }
        if (event.userFullName) {
            label = `${label} ${this.i18nService.translate('demandes-financement.by')} ${event.userFullName}`;
        }
        const formattedEvent = { label, status: event.status };
        if (event.label)
            formattedEvent.customStatusTitle = event.label;
        return formattedEvent;
    }
    buildDemandeApprenantSubActions(demandeFinancement, result) {
        // Sub Actions
        if (demandeFinancement.secondaryActions) {
            for (const action of demandeFinancement.secondaryActions) {
                switch (action.type) {
                    // CONTRIBUTION_INFORMATION
                    case DemandesFinancementActions.CONTRIBUTION_INFORMATION: {
                        result.subactions.push({
                            id: `${DemandesFinancementActions.CONTRIBUTION_INFORMATION}:${action.href}`,
                            title: action.value,
                            icon: { icon: 'pen' },
                            href: action.href,
                        });
                        break;
                    }
                }
            }
        }
    }
    /**
     * build virtual history event from demande and virtualHistory attribute
     */
    async buildVirtualHistory(demandeApprenant) {
        const virtualHistory = [];
        // Close event
        if (demandeApprenant.cloture?.value === true) {
            virtualHistory.push({
                status: DemandesFinancementStatus.closed,
                label: await this.demandesFinancementFormatter.translateEventDate('demandes-financement.at', demandeApprenant.cloture.date),
            });
        }
        // Events should be ordered by date
        // ! Exception for VERIFIED/ENTERED_FORMATION or VERIFIED/NOT_ENTERED_FORMATION, because they can have the same date, but VERIFIED should be before
        const events = (demandeApprenant.virtualHistory ?? []).sort((a, b) => {
            const firstDate = a.date.length > 0 ? a.date : new Date(0);
            const secondDate = b.date.length > 0 ? b.date : new Date(0);
            const sortByDate = new Date(secondDate).getTime() - new Date(firstDate).getTime();
            if (sortByDate === 0) {
                const validationEvents = new Set([
                    BourseVirtualStatus.ENTERED_FORMATION,
                    BourseVirtualStatus.NOT_ENTERED_FORMATION,
                ]);
                // ! Warning : PLAID-107077
                if (b.status === BourseVirtualStatus.VERIFIED && validationEvents.has(a.status))
                    return -1;
                else if (a.status === BourseVirtualStatus.VERIFIED && validationEvents.has(b.status))
                    return 1;
                else
                    return 0;
            }
            return sortByDate;
        });
        for (const event of events) {
            virtualHistory.push(await this.formatHistoryEvent(event));
        }
        return virtualHistory;
    }
    /**
     * format a demande apprenant to be used by a front component
     */
    async formatDemandeApprenant(demandeApprenant) {
        const result = {
            id: demandeApprenant.id,
            title: demandeApprenant.title,
            reference: demandeApprenant.reference,
            referenceAdministrative: demandeApprenant.referenceAdministrative,
            actions: [],
            subactions: [],
            subActionsBtnLabel: this.i18nService.translate('demandes-financement.sub-actions-btn-label'),
            virtualHistoryDetails: [],
            teleservice: demandeApprenant.teleservice,
        };
        if (demandeApprenant.bourse?.formationDateEntree) {
            result.bourse = {
                formationDateEntree: demandeApprenant.bourse.formationDateEntree,
            };
        }
        if (demandeApprenant.contributionVef?.status) {
            result.contributionVef = {
                status: demandeApprenant.contributionVef?.status,
            };
        }
        if (demandeApprenant.cloture?.value === true) {
            result.status = this.i18nService.translate(`connected.dashboard.aides.list.state-CLOTURE`);
            result.cloture = demandeApprenant.cloture;
        }
        result.virtualHistoryDetails = await this.buildVirtualHistory(demandeApprenant);
        this.demandesFinancementFormatter.buildDemandeFinancementActions(demandeApprenant, result);
        // title reference adminisrative
        if (demandeApprenant.referenceAdministrative) {
            result.title += ` (${demandeApprenant.referenceAdministrative})`;
        }
        this.buildDemandeApprenantSubActions(demandeApprenant, result);
        return result;
    }
    /**
     * format a demandes apprenants list to be used by a front component
     */
    async formatDemandesApprenantsList(demandesApprenants) {
        return await Promise.all(demandesApprenants.map(async (demandeApprenant) => {
            return await this.formatDemandeApprenant(demandeApprenant);
        }));
    }
}
export { DemandesApprenantsFormatter };
//# sourceMappingURL=demandes-apprenants-formatter.js.map